import { useState, type ChangeEvent, type FormEvent } from 'react';
import { TextField } from '@/components/forms/TextField';
import { PhoneField } from '@/components/forms/PhoneField';
import { Button } from '@/components/ui/Button';
import { ChecklistCheckIcon } from '@/assets/icons/ChecklistCheckIcon';
import { getApiErrorMessage } from '@/lib/utils/error.utils';
import { phoneSchema } from '@/lib/utils/phone.utils';
import { normalizeUrl } from '@/lib/utils/url.utils';
import { useSubmitApplication } from '../../hooks/useSubmitApplication';
import type { PublicApplicationType } from '../../api/applications.public.api';
import type { SectionRendererProps } from '../section-renderer.types';

/**
 * Which public Apply-form route a page submits to. Keyed by `pageSlug`, not
 * `section.key`, per this codebase's established per-page lookup idiom - safe
 * because a page slug is never reused across pages, unlike a section key.
 * 'trusted-partners' is listed ahead of its own page shipping (MYN-215/216)
 * since this renderer is already built to be reused there unchanged.
 */
const PAGE_SLUG_TO_APPLICATION_TYPE: Record<string, PublicApplicationType> = {
  investors: 'investor',
  'trusted-partners': 'trusted-partner',
};

/**
 * Pages where the left-hand checklist column and the "member offers" line
 * do not render. Keyed by `pageSlug`, same idiom as the map above - the
 * thing varying is "which page", not which section.
 *
 * Investors first (MYN-328, FR-314, design §56 D3): that copy describes
 * Trusted Partners and does not apply to an investor. Trusted Partners
 * followed (MYN-327, FR-322, design §57 D6) once its own content document
 * dropped the column too - and with it the "Free to join" and "within 5
 * business days" claims FR-324 forbids. Both pages now centre the form in
 * the space the column leaves.
 */
const NO_INFO_COLUMN = new Set(['investors', 'trusted-partners']);

/**
 * Pages whose form collects city, state and country (MYN-327, FR-322).
 *
 * Only Investors does. The Trusted Partners content document lists six
 * fields, and its backend route rejects the other three outright rather
 * than ignoring them (FR-323), so they must not be rendered OR submitted
 * there - a blank string would be rejected just as a value would.
 */
const COLLECTS_LOCATION = new Set(['investors']);

interface ApplicationFormContent {
  eyebrow?: string;
  titleHighlight: string;
  titleSuffix: string;
  checklist: string[];
}

interface ApplicationFormFields {
  firstName: string;
  lastName: string;
  companyName: string;
  businessEmail: string;
  website: string;
  phone: string;
  city: string;
  state: string;
  country: string;
}

const EMPTY_FIELDS: ApplicationFormFields = {
  firstName: '',
  lastName: '',
  companyName: '',
  businessEmail: '',
  website: '',
  phone: '',
  city: '',
  state: '',
  country: '',
};

/**
 * `state`/`country` are free text, not dropdowns (MYN-201, FR-160) - this is
 * what lets a non-US applicant type "London" / "England" / "United Kingdom"
 * with no extra validation or picker.
 */
const FIELD_LABELS: Record<keyof ApplicationFormFields, string> = {
  firstName: 'First name',
  lastName: 'Last Name',
  companyName: 'Company Name',
  businessEmail: 'Business Email',
  website: 'Website',
  phone: 'Phone',
  city: 'City',
  state: 'State / Region',
  country: 'Country',
};

const requiredPhone = phoneSchema(true);

/** The three fields only some pages collect - skipped entirely elsewhere. */
const LOCATION_FIELDS = ['city', 'state', 'country'] as const satisfies readonly (keyof ApplicationFormFields)[];

function validate(
  fields: ApplicationFormFields,
  collectsLocation: boolean,
): Partial<Record<keyof ApplicationFormFields, string>> {
  const errors: Partial<Record<keyof ApplicationFormFields, string>> = {};
  for (const key of Object.keys(FIELD_LABELS) as (keyof ApplicationFormFields)[]) {
    if (key === 'phone') continue;
    // A page that never renders these cannot be blocked on them (FR-322).
    if (!collectsLocation && (LOCATION_FIELDS as readonly string[]).includes(key)) continue;
    if (!fields[key].trim()) {
      errors[key] = `${FIELD_LABELS[key]} is required.`;
    }
  }
  // Phone stays required and must be a valid E.164 number for the chosen country (MYN-277, FR-184).
  const phone = requiredPhone.safeParse(fields.phone);
  if (!phone.success) errors.phone = phone.error.issues[0]?.message;
  return errors;
}

/**
 * Apply-to-Become-an-Investor section (FR-136). Colors/layout confirmed via
 * Figma's own get_design_context data, not assumed: teal (#00405C) section
 * background, white "APPLY TODAY" eyebrow (Figma's own data literally
 * specifies this eyebrow in the same teal as the background, which would
 * render invisible - white matches every other eyebrow-on-dark-background
 * in this design system and is treated as a Figma authoring error, not a
 * style to reproduce), dkgreen title with "Apply" in white (the client's
 * correction), checklist items as white bordered rows with a checkmark icon
 * (not a plain bulleted list), and a cream-to-mint gradient form panel with
 * floating-label fields (`TextField`'s `inset` variant).
 *
 * The form's fields are hardcoded here, not CMS content (A-116) - only the
 * eyebrow, two-tone title, and checklist items come from the CMS.
 *
 * Submission posts to the public Apply-form endpoint (MYN-80, FR-140) via
 * `useSubmitApplication`, keyed off `pageSlug` to the matching
 * `PublicApplicationType` - no CMS content or schema change, exactly as
 * anticipated when the form was first built client-side-only.
 */
export function ApplicationFormRenderer({ section, pageSlug }: SectionRendererProps) {
  const c = section.content as unknown as ApplicationFormContent;
  const [fields, setFields] = useState<ApplicationFormFields>(EMPTY_FIELDS);
  const [errors, setErrors] = useState<Partial<Record<keyof ApplicationFormFields, string>>>({});
  const applicationType = PAGE_SLUG_TO_APPLICATION_TYPE[pageSlug] ?? 'investor';
  const mutation = useSubmitApplication(applicationType);
  const collectsLocation = COLLECTS_LOCATION.has(pageSlug);

  function handleFieldChange(key: keyof ApplicationFormFields) {
    return (event: ChangeEvent<HTMLInputElement>) => {
      setFields((prev) => ({ ...prev, [key]: event.target.value }));
    };
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors = validate(fields, collectsLocation);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    const { city, state, country, ...shared } = fields;
    // Accepts a bare host with no scheme (MYN-301, FR-219): normalized here,
    // and defensively on the backend too (`normalizeWebsiteUrl` in
    // create-public-application.dto.ts), the same precedent as Partner Offers.
    const payload = { ...shared, website: normalizeUrl(shared.website) };
    // Omitted entirely, not sent blank: the trusted-partner route rejects
    // these three under `forbidNonWhitelisted` (MYN-327, FR-323).
    mutation.mutate(collectsLocation ? { ...payload, city, state, country } : payload);
  }

  // MYN-328 (FR-314, design §56 D3): Investors drops the left-hand checklist
  // column and the "member offers" line below - both describe Trusted
  // Partners, not an investor. The eyebrow/title stay (they're this page's
  // own CMS content, not column decoration) but move above the form instead
  // of beside it, and the form panel centres in the space that leaves.
  const noInfoColumn = NO_INFO_COLUMN.has(pageSlug);
  // Keyed to this section's own id (not a literal string) so two instances
  // of this renderer on one screen - an admin preview, say - never collide.
  const formId = `apply-form-${section.id}`;
  const eyebrowAndTitle = (
    <>
      {c.eyebrow && <p className="text-p6 font-semibold text-white uppercase">{c.eyebrow}</p>}
      <h2 className="text-h2">
        <span className="text-white">{c.titleHighlight}</span>
        <span className="text-dkgreen">{c.titleSuffix}</span>
      </h2>
    </>
  );

  return (
    <section id="apply" className="bg-teal w-full">
      <div
        className={
          noInfoColumn
            ? 'mx-auto flex w-full max-w-xl flex-col items-center gap-10 px-4 py-16 text-center lg:py-24'
            : 'mx-auto flex w-full max-w-328 flex-col items-start gap-10 px-4 py-16 lg:flex-row lg:py-24'
        }
      >
        {noInfoColumn ? (
          <div className="flex flex-col items-center gap-6">{eyebrowAndTitle}</div>
        ) : (
          <div className="flex flex-col gap-6 lg:max-w-md">
            {eyebrowAndTitle}
            <ul className="flex flex-col">
              {c.checklist?.map((item, i) => (
                <li
                  key={i}
                  className="border-slate/50 flex items-center gap-4 border-b bg-white p-6 first:rounded-t-xl last:rounded-b-xl last:border-0"
                >
                  <ChecklistCheckIcon className="size-6 shrink-0 text-green" />
                  <span className="text-p4 font-medium text-ink">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="flex w-full flex-1 flex-col gap-6 text-left">
          <div className="rounded-2xl bg-gradient-to-b from-[#f9f8f3] to-[#f5fdf9] p-8">
            {mutation.isSuccess ? (
              <p role="status" className="text-p4 text-text-primary">
                Thanks, we&apos;ve received your details and will be in touch in a few days.
              </p>
            ) : (
              <form id={formId} onSubmit={handleSubmit} noValidate className="flex flex-col gap-6">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <TextField
                    inset
                    label={FIELD_LABELS.firstName}
                    required
                    value={fields.firstName}
                    onChange={handleFieldChange('firstName')}
                    error={errors.firstName}
                  />
                  <TextField
                    inset
                    label={FIELD_LABELS.lastName}
                    required
                    value={fields.lastName}
                    onChange={handleFieldChange('lastName')}
                    error={errors.lastName}
                  />
                </div>
                <TextField
                  inset
                  label={FIELD_LABELS.companyName}
                  required
                  value={fields.companyName}
                  onChange={handleFieldChange('companyName')}
                  error={errors.companyName}
                />
                <TextField
                  inset
                  label={FIELD_LABELS.businessEmail}
                  type="email"
                  required
                  value={fields.businessEmail}
                  onChange={handleFieldChange('businessEmail')}
                  error={errors.businessEmail}
                />
                <TextField
                  inset
                  label={FIELD_LABELS.website}
                  required
                  value={fields.website}
                  onChange={handleFieldChange('website')}
                  error={errors.website}
                />
                <PhoneField
                  inset
                  label={FIELD_LABELS.phone}
                  required
                  value={fields.phone}
                  onChange={(phone) => setFields((prev) => ({ ...prev, phone }))}
                  error={errors.phone}
                  defaultCountryIso2="US"
                />
                {/* Investors only (MYN-327, FR-322): the Trusted Partners
                  content document lists six fields, and its route rejects
                  these three rather than ignoring them. */}
                {collectsLocation && (
                  <>
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      <TextField
                        inset
                        label={FIELD_LABELS.city}
                        required
                        value={fields.city}
                        onChange={handleFieldChange('city')}
                        error={errors.city}
                      />
                      <TextField
                        inset
                        label={FIELD_LABELS.state}
                        required
                        value={fields.state}
                        onChange={handleFieldChange('state')}
                        error={errors.state}
                      />
                    </div>
                    <TextField
                      inset
                      label={FIELD_LABELS.country}
                      required
                      value={fields.country}
                      onChange={handleFieldChange('country')}
                      error={errors.country}
                    />
                  </>
                )}
                {!noInfoColumn && (
                  <p className="text-[13px] text-white">
                    Partners with member offers see significantly higher profile engagement.
                  </p>
                )}
                {mutation.isError && (
                  <p role="alert" className="text-p6 font-medium text-danger">
                    {getApiErrorMessage(mutation.error, 'Something went wrong submitting your application. Please try again.')}
                  </p>
                )}
              </form>
            )}
          </div>
          {/* Submit lives outside the white card, in the teal space below it -
            client instruction, not a Figma source. `form={formId}` is what
            still lets this button submit the form from outside its markup:
            a native HTML association (MDN button[form]), not a JS hookup, so
            Enter-to-submit and the browser's own validation UX are unaffected. */}
          {!mutation.isSuccess && (
            <Button type="submit" form={formId} variant="primary" className="w-full" disabled={mutation.isPending}>
              {mutation.isPending ? 'Submitting...' : 'Submit Application'}
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}
